import { decimalsFor, formatLength, gridSnapStep, rulerStep, toUnit, type Guide, type LengthUnit, type Rect } from '@flobi/artwork-core';
import type { Camera } from '@flobi/artwork-core/render';

/**
 * Just enough of the text engine to print a number on a ruler.
 *
 * Named structurally rather than taken as the whole renderer, because that is genuinely
 * all this wants: the shaper it is really a `ParagraphText`, but a ruler asking for the
 * full interface would be a ruler that cannot be drawn without one.
 */
interface Labeller {
  label(
    canvas: Canvas,
    text: string,
    at: { x: number; y: number },
    options?: { size?: number; color?: readonly [number, number, number, number]; weight?: number },
  ): unknown;
  /** What a label would take up, without drawing it. */
  labelSize?(text: string, options?: { size?: number; weight?: number }): { width: number; height: number };
}
import type { Canvas, CanvasKit, Image, Path, Shader } from 'canvaskit-wasm';
import { hexColor } from './colors';
import { MARK, MARK_GLYPH, markPlace } from './movingBoards';
import { fitLabel, LABEL, labelSlot, labelText, type LabelBox } from './boardLabels';
import { rulerLayout, type RulerInset } from './rulerLayout';
import type { Palette } from '../theme';

/**
 * The furniture: the rulers, the grid, the checkerboard and the guides.
 *
 * Everything here is drawn in SCREEN pixels against the camera, not in document
 * coordinates — which is exactly what makes it furniture rather than artwork. A ruler
 * does not zoom, a guide is one pixel wide at any magnification, and the checkerboard
 * is a texture on the glass and not something painted onto the page. That is the whole
 * reason this is its own file and does not go through the `Overlay` a tool draws with:
 * every method there takes document coordinates, because a tool is drawing ABOUT the
 * artwork, and none of this is.
 *
 * Two of the four sit on opposite sides of the artwork, and the difference matters. The
 * checkerboard and the grid are things the page sits ON, so they go in the renderer's
 * backdrop — between clearing the frame and compositing the tiles — or the grid would
 * lay hairlines across every filled shape on the page. The rulers and the guides are
 * the editor talking, so they go on top with the handles and the snap lines.
 */

/** How near a guide the pointer has to be to take hold of it, in CSS pixels. */
export const GUIDE_REACH = 4;
/** The numbered ones stop being drawn below this, so a zoomed-out page stays legible. */
const LEAST_STEP_PIXELS = 3;

export interface Sheet {
  readonly camera: Camera;
  /** The view in CSS pixels. */
  readonly width: number;
  readonly height: number;
  readonly dpr: number;
  /**
   * Where the ruler corner sits, in CSS pixels from the top-left of the canvas.
   *
   * The canvas fills the window and the chrome floats over it, which is the whole shape
   * of this editor — so a ruler hugging the window's own edge would spend its first
   * forty pixels behind the toolbar and its middle behind the left rail, which is
   * exactly what it did before this existed. The rulers frame the WORKING area instead,
   * and in full-screen, where there is no chrome, that is the window after all.
   */
  readonly inset: RulerInset;
}

export interface GridSettings {
  readonly step: number;
  readonly divisions: number;
}

/** A guide the drag is currently landing on, so it can be drawn as having caught. */
export interface LitGuide {
  readonly axis: 'x' | 'y';
  readonly at: number;
}

/**
 * Cyan, which is what a guide has been in every drawing program since the eighties, and
 * deliberately NOT the colour of a snap line. The two say different things — "there is
 * a line here" against "the thing you are dragging agrees with that thing over there" —
 * and drawn alike they would read as one thing behaving inconsistently.
 */
const GUIDE = { r: 0.1, g: 0.72, b: 0.85 };

/** The line round the page being worked on, in CSS pixels: how thick, and how far outside the page's edge. */
/** One screen pixel, quiet (Tarek, 15 Oct: 1.5 px of full ink was "so thick and annoying"). */
export const WORKING = { width: 1, gap: 2, alpha: 0.4 } as const;

/**
 * Whether the theme in force is the light one, asked of the canvas colour itself.
 *
 * Asked rather than told: the palette is the one thing both themes definitely have, and
 * a grid drawn in black on a black canvas is invisible in a way nobody would report as
 * a bug — they would simply say the grid does not work.
 */
function lightGround(palette: Palette): boolean {
  const { r, g, b } = hexColor(palette.canvas);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.5;
}

export class Furniture {
  private checker: { image: Image; shader: Shader; dpr: number; light: boolean } | null = null;
  private mark: Path | null = null;
  private readonly paint;
  private readonly fill;

  constructor(
    private readonly ck: CanvasKit,
    private readonly palette: () => Palette,
  ) {
    this.paint = new ck.Paint();
    this.paint.setAntiAlias(false);
    this.paint.setStyle(ck.PaintStyle.Stroke);
    this.fill = new ck.Paint();
    this.fill.setAntiAlias(true);
    this.fill.setStyle(ck.PaintStyle.Fill);
  }

  dispose(): void {
    this.checker?.shader.delete();
    this.checker?.image.delete();
    this.checker = null;
    this.mark?.delete();
    this.mark = null;
    this.paint.delete();
    this.fill.delete();
  }

  // ── behind the artwork ────────────────────────────────────────────────────

  /**
   * The checkerboard and the grid, in that order, drawn straight onto the frame.
   *
   * Called from the renderer's backdrop, so everything the document draws lands on top
   * of it. Nothing here is antialiased: a grid line is one device pixel and wants to BE
   * one device pixel, and a half-covered pair of rows is how a grid turns into a haze.
   */
  drawBackdrop(canvas: Canvas, sheet: Sheet, options: { grid: boolean; transparency: boolean; settings: GridSettings }): void {
    if (options.transparency) this.drawChecker(canvas, sheet);
    if (options.grid) this.drawGrid(canvas, sheet, options.settings);
  }

  /** Quiet world-anchored dots sit behind artwork, and never enter an export. */
  drawDots(canvas: Canvas, sheet: Sheet): void {
    const { camera, dpr, width, height } = sheet;
    const step = 24 * Math.pow(2, -Math.floor(Math.log2(camera.zoom)));
    const pixels = step * camera.zoom;
    const offsetX = ((-camera.x * camera.zoom) % pixels + pixels) % pixels;
    const offsetY = ((-camera.y * camera.zoom) % pixels + pixels) % pixels;
    const points: number[] = [];
    for (let y = offsetY; y < height; y += pixels) {
      for (let x = offsetX; x < width; x += pixels) points.push(x * dpr, y * dpr);
    }
    const ink = lightGround(this.palette()) ? 0 : 1;
    this.paint.setColor(this.ck.Color4f(ink, ink, ink, .16));
    this.paint.setStrokeWidth(dpr);
    canvas.drawPoints(this.ck.PointMode.Points, points, this.paint);
  }

  private drawChecker(canvas: Canvas, sheet: Sheet): void {
    const shader = this.checkerShader(sheet.dpr);
    if (!shader) return;
    this.fill.setShader(shader);
    this.fill.setAlphaf(1);
    canvas.drawRect(this.ck.XYWHRect(0, 0, sheet.width * sheet.dpr, sheet.height * sheet.dpr), this.fill);
    this.fill.setShader(null);
  }

  /**
   * The checkerboard, as one tile repeated by the graphics card.
   *
   * The same bargain the stipple makes, and for the same reason: a screenful of squares
   * is a PATTERN, and the way to draw a pattern is to hand over one tile. Anchored to
   * the surface rather than to the document, so it stays still while the artwork moves
   * over it — a checkerboard that slid about with the page would read as a texture
   * somebody had painted on, which is the one thing it must not look like.
   */
  private checkerShader(dpr: number): Shader | null {
    const ck = this.ck;
    const light = lightGround(this.palette());
    if (this.checker && this.checker.dpr === dpr && this.checker.light === light) return this.checker.shader;
    this.checker?.shader.delete();
    this.checker?.image.delete();
    this.checker = null;
    // Eight CSS pixels a square, which is Photoshop's medium and the size at which a
    // checkerboard reads as "nothing here" rather than as a pattern in its own right.
    const square = Math.max(4, Math.round(8 * dpr));
    const tile = ck.MakeSurface(square * 2, square * 2);
    if (!tile) return null;
    const face = tile.getCanvas();
    const pale = light ? 1 : 0.34;
    const dim = light ? 0.86 : 0.26;
    face.clear(ck.Color4f(pale, pale, pale, 1));
    const paint = new ck.Paint();
    paint.setAntiAlias(false);
    paint.setColor(ck.Color4f(dim, dim, dim, 1));
    face.drawRect(ck.XYWHRect(0, 0, square, square), paint);
    face.drawRect(ck.XYWHRect(square, square, square, square), paint);
    paint.delete();
    const image = tile.makeImageSnapshot();
    tile.delete();
    const shader = image.makeShaderOptions(ck.TileMode.Repeat, ck.TileMode.Repeat, ck.FilterMode.Nearest, ck.MipmapMode.None);
    this.checker = { image, shader, dpr, light };
    return shader;
  }

  private drawGrid(canvas: Canvas, sheet: Sheet, settings: GridSettings): void {
    const ck = this.ck;
    const { camera, dpr } = sheet;
    if (!(settings.step > 0) || !Number.isFinite(settings.step)) return;
    /**
     * The finest level of the grid worth drawing at this magnification.
     *
     * Not the subdivision itself: it climbs, by the same rule and through the same
     * function that decides which of the grid's lines a drag is rounded onto. That is
     * the point of sharing it — the coarsest thing you snap to is always a line that is
     * actually on the screen, so "it snapped to nothing" can never happen. It also
     * stops the grid dropping every faint line at once on the way out, which is what it
     * used to do: one moment a nine-unit mesh, the next only the numbered lines.
     */
    //
    // Exactly the call snapping makes (`snapSettingsOf`), threshold and all. It used to
    // climb at 4 px here and 8 px there, and with ÷6 or ÷10 the two picked levels that do
    // not line up, so shapes parked between the visible lines (QA 2026-09-24,
    // view-snapping-F8).
    const fine = gridSnapStep(settings.step, settings.divisions, 1 / camera.zoom);

    const light = lightGround(this.palette());
    const ink = light ? 0 : 1;
    const wide = sheet.width * dpr;
    const tall = sheet.height * dpr;
    const toX = (x: number) => Math.round((x - camera.x) * camera.zoom * dpr) + 0.5;
    const toY = (y: number) => Math.round((y - camera.y) * camera.zoom * dpr) + 0.5;
    const left = camera.x;
    const right = camera.x + sheet.width / camera.zoom;
    const top = camera.y;
    const bottom = camera.y + sheet.height / camera.zoom;

    this.paint.setStrokeWidth(dpr);
    // The faint ones first, so a numbered line drawn over one still reads as the
    // numbered line rather than as whichever happened to be drawn last. Skipped once
    // the level has climbed to the numbered line itself, which is already drawn below.
    if (fine > 0 && fine < settings.step) {
      this.paint.setColor(ck.Color4f(ink, ink, ink, light ? 0.07 : 0.09));
      for (let x = Math.ceil(left / fine) * fine; x <= right; x += fine) canvas.drawLine(toX(x), 0, toX(x), tall, this.paint);
      for (let y = Math.ceil(top / fine) * fine; y <= bottom; y += fine) canvas.drawLine(0, toY(y), wide, toY(y), this.paint);
    }
    // Once the level has climbed past the gridline it is a whole number of gridlines, and
    // those are what is drawn: snapping still lands on them, so drawing nothing there
    // left shapes jumping in steps nobody could see (view-snapping-F8).
    const major = fine > settings.step ? fine : settings.step;
    if (major * camera.zoom >= LEAST_STEP_PIXELS) {
      this.paint.setColor(ck.Color4f(ink, ink, ink, light ? 0.16 : 0.2));
      for (let x = Math.ceil(left / major) * major; x <= right; x += major) {
        canvas.drawLine(toX(x), 0, toX(x), tall, this.paint);
      }
      for (let y = Math.ceil(top / major) * major; y <= bottom; y += major) {
        canvas.drawLine(0, toY(y), wide, toY(y), this.paint);
      }
    }
  }

  // ── in front of it ────────────────────────────────────────────────────────

  /**
   * The mark on every artboard that moves: a small play button in a box, outside its top
   * right corner, which plays the page when pressed.
   *
   * In the quiet ink rather than the selection's colour: it says something about the board
   * that is true whether or not the board is chosen, so it must not look like part of
   * choosing it. Under the pointer it takes the full ink, which is how it says it can be
   * pressed.
   */
  drawMovingMarks(canvas: Canvas, sheet: Sheet, boards: readonly { box: Rect; lit: boolean }[]): void {
    if (boards.length === 0) return;
    const ck = this.ck;
    const { dpr } = sheet;
    const palette = this.palette();
    for (const { box, lit } of boards) {
      const place = markPlace(box, sheet.camera);
      // Off the view altogether: nothing to draw, and on a long file that is most of them.
      if (!place || place.x > sheet.width || place.y > sheet.height || place.x < -MARK.size || place.y < -MARK.size) continue;
      const ink = hexColor(lit ? palette.ink : palette.inkMuted);
      this.fill.setColor(ck.Color4f(ink.r, ink.g, ink.b, 1));
      canvas.save();
      // On whole device pixels, so the box's straight edges stay edges while the page pans.
      canvas.translate(Math.round(place.x * dpr), Math.round(place.y * dpr));
      canvas.scale((MARK.size * dpr) / 24, (MARK.size * dpr) / 24);
      const glyph = this.markGlyph();
      if (glyph) canvas.drawPath(glyph, this.fill);
      canvas.restore();
    }
  }

  /**
   * Each artboard's name, just above its top left corner (see `boardLabels.ts`).
   *
   * Quiet ink at rest, the full ink under the pointer, and the selection's colour while the
   * board is chosen — the one part of this file that does wear that colour, because here
   * the name IS the thing being chosen. Answers where each name landed, in CSS pixels, so
   * a press can be matched against exactly what was drawn.
   */
  drawBoardLabels(
    canvas: Canvas,
    sheet: Sheet,
    text: Labeller,
    boards: readonly { id: string; box: Rect; name: string | undefined; tone: 'quiet' | 'lit' | 'chosen'; marked: boolean }[],
  ): LabelBox[] {
    const drawn: LabelBox[] = [];
    if (boards.length === 0 || !text.labelSize) return drawn;
    const { dpr } = sheet;
    const palette = this.palette();
    const size = LABEL.size * dpr;
    const measure = (said: string) => text.labelSize!(said, { size, weight: 500 }).width / dpr;
    const height = text.labelSize('Ag', { size, weight: 500 }).height / dpr;
    for (const { id, box, name, tone, marked } of boards) {
      const slot = labelSlot(box, sheet.camera, height, marked);
      // Off the view altogether: nothing to draw, and on a long file that is most of them.
      if (!slot || slot.x > sheet.width || slot.y > sheet.height || slot.x + slot.room < 0 || slot.y + height < 0) continue;
      const fitted = this.fitted(labelText(name), slot.room, measure);
      if (!fitted) continue;
      const ink = hexColor(tone === 'chosen' ? palette.selection : tone === 'lit' ? palette.ink : palette.inkMuted);
      text.label(canvas, fitted, { x: Math.round(slot.x * dpr), y: Math.round(slot.y * dpr) }, { size, weight: 500, color: [ink.r, ink.g, ink.b, 1] });
      drawn.push({ id, x: slot.x, y: slot.y, width: Math.min(measure(fitted), slot.room), height });
    }
    return drawn;
  }

  /** Names cut to fit, kept: the cut is a handful of measurements, and the frame is drawn sixty times a second. */
  private readonly fits = new Map<string, string>();
  private fitted(said: string, room: number, measure: (text: string) => number): string {
    const key = `${Math.floor(room)}\u0000${said}`;
    const known = this.fits.get(key);
    if (known !== undefined) return known;
    if (this.fits.size > 512) this.fits.clear();
    const fitted = fitLabel(said, Math.floor(room), measure);
    this.fits.set(key, fitted);
    return fitted;
  }

  /**
   * The page being worked on: a line round it, a little way OUTSIDE its edge, so not one
   * pixel of the design is covered and it reads on a white page and a black one alike (Tarek,
   * 2026-10-01: "I don't know which artboard I'm working with… a black outline around it
   * maybe? that way we don't ruin people's design").
   *
   * In the ink, which is the colour that stands out from the CANVAS — black on the light
   * one, white on the dark — never the selection's colour: it says where things go, not
   * what is chosen. The gap is what keeps it apart from the page when the two are alike.
   */
  drawWorkingBoard(canvas: Canvas, sheet: Sheet, box: Rect): void {
    const ck = this.ck;
    const { camera, dpr } = sheet;
    const ink = hexColor(this.palette().ink);
    const width = Math.max(1, Math.round(WORKING.width * dpr));
    const out = Math.round(WORKING.gap * dpr) + width / 2;
    const x = (v: number) => Math.round((v - camera.x) * camera.zoom * dpr);
    const y = (v: number) => Math.round((v - camera.y) * camera.zoom * dpr);
    this.paint.setColor(ck.Color4f(ink.r, ink.g, ink.b, WORKING.alpha));
    this.paint.setStrokeWidth(width);
    canvas.drawRect(ck.LTRBRect(x(box.x) - out, y(box.y) - out, x(box.x + box.width) + out, y(box.y + box.height) + out), this.paint);
  }

  /** The mark's glyph, read once from its SVG path on its 24-unit grid. */
  private markGlyph(): Path | null {
    if (this.mark) return this.mark;
    const glyph = this.ck.Path.MakeFromSVGString(MARK_GLYPH);
    if (!glyph) return null;
    glyph.setFillType(this.ck.FillType.EvenOdd);
    this.mark = glyph;
    return glyph;
  }

  /**
   * The guides, each one line across the whole view.
   *
   * A guide runs the width of the page and not of the artwork, because the thing it is
   * for is lining up what is not there yet. `lit` is whatever a drag is landing on right
   * now, drawn heavier — a guide that has caught something should say so, or the drag
   * looks like it stopped for no reason.
   */
  drawGuides(canvas: Canvas, sheet: Sheet, guides: readonly Guide[], options: { locked: boolean; held: string | null; lit: readonly LitGuide[] }): void {
    if (guides.length === 0) return;
    const ck = this.ck;
    const { camera, dpr } = sheet;
    const wide = sheet.width * dpr;
    const tall = sheet.height * dpr;
    this.paint.setStrokeWidth(dpr);
    const dashes = options.locked ? ck.PathEffect.MakeDash([3 * dpr, 3 * dpr]) : null;
    this.paint.setPathEffect(dashes);
    for (const guide of guides) {
      const caught = options.held === guide.id || options.lit.some((one) => one.axis === guide.axis && Math.abs(one.at - guide.at) < 0.01);
      this.paint.setColor(ck.Color4f(GUIDE.r, GUIDE.g, GUIDE.b, caught ? 1 : 0.65));
      this.paint.setStrokeWidth((caught ? 2 : 1) * dpr);
      if (guide.axis === 'x') {
        const x = Math.round((guide.at - camera.x) * camera.zoom * dpr) + 0.5;
        if (x >= 0 && x <= wide) canvas.drawLine(x, 0, x, tall, this.paint);
      } else {
        const y = Math.round((guide.at - camera.y) * camera.zoom * dpr) + 0.5;
        if (y >= 0 && y <= tall) canvas.drawLine(0, y, wide, y, this.paint);
      }

    }
    this.paint.setPathEffect(null);
    dashes?.delete();
  }

  /** Floating rails share their exact screen bounds with guide hit testing. */
  drawRulers(canvas: Canvas, sheet: Sheet, options: {
    unit: LengthUnit; pointer: { x: number; y: number } | null; text: Labeller | null;
    selection: { x: number; y: number; width: number; height: number } | null;
  }): void {
    const ck = this.ck;
    const { camera, dpr } = sheet;
    const layout = rulerLayout(sheet.width, sheet.height, sheet.inset);
    const palette = this.palette();
    const surface = hexColor(palette.surface);
    const line = hexColor(palette.line);
    const muted = hexColor(palette.inkMuted);
    const accent = hexColor(palette.selection);
    const step = rulerStep(options.unit, 1 / camera.zoom);
    const ticks = step / 5;
    const ruled = options.unit === 'ft-in' ? 'in' : options.unit;
    const needed = Math.max(0, Math.ceil(-Math.log10(toUnit(step, ruled)) - 1e-9));
    const decimals = Math.max(options.unit === 'ft-in' ? 1 : decimalsFor(ruled, true), needed);
    const said = (value: number) => formatLength(value, options.unit, { bare: true, compact: true, decimals });
    const ink: readonly [number, number, number, number] = [muted.r, muted.g, muted.b, 1];
    this.fill.setShader(null);
    this.fill.setAlphaf(1);
    this.paint.setStrokeWidth(dpr);
    for (const [kind, rect] of Object.entries(layout)) {
      if (!rect) continue;
      const x = rect.x * dpr, y = rect.y * dpr, w = rect.width * dpr, h = rect.height * dpr;
      // A flat band on the panel's surface with one hairline where it meets the canvas:
      // the bottom of the horizontal band, the right of the vertical one, both for the
      // corner. No outer border, no rounding — the band is part of the chrome's edge.
      const band = ck.XYWHRect(x, y, w, h);
      this.fill.setColor(ck.Color4f(surface.r, surface.g, surface.b, 1));
      canvas.drawRect(band, this.fill);
      canvas.save();
      canvas.clipRect(band, ck.ClipOp.Intersect, true);
      this.paint.setColor(ck.Color4f(line.r, line.g, line.b, 0.7));
      if (kind !== 'vertical') canvas.drawLine(x, y + h - 0.5 * dpr, x + w, y + h - 0.5 * dpr, this.paint);
      if (kind !== 'horizontal') canvas.drawLine(x + w - 0.5 * dpr, y, x + w - 0.5 * dpr, y + h, this.paint);
      if (kind === 'corner') {
        // RulerUnits supplies the accessible menu trigger over this painted square.
        const long = options.unit.length > 3;
        options.text?.label(canvas, options.unit, { x: x + (long ? 1.5 : 4.5) * dpr, y: y + (long ? 6 : 5) * dpr }, { size: (long ? 7 : 9) * dpr, color: ink, weight: 500 });
        canvas.restore();
        continue;
      }
      const horizontal = kind === 'horizontal';
      const origin = horizontal ? camera.x : camera.y;
      const start = horizontal ? rect.x : rect.y;
      const length = horizontal ? rect.width : rect.height;
      const toScreen = (v: number) => Math.round((v - origin) * camera.zoom * dpr) + 0.5;
      if (options.selection) {
        const from = horizontal ? options.selection.x : options.selection.y;
        const size = horizontal ? options.selection.width : options.selection.height;
        this.fill.setColor(ck.Color4f(accent.r, accent.g, accent.b, 0.16));
        canvas.drawRect(horizontal ? ck.XYWHRect(toScreen(from), y, size * camera.zoom * dpr, h)
          : ck.XYWHRect(x, toScreen(from), w, size * camera.zoom * dpr), this.fill);
      }
      const low = origin + start / camera.zoom;
      const high = origin + (start + length) / camera.zoom;
      this.paint.setColor(ck.Color4f(muted.r, muted.g, muted.b, 0.25));
      for (let n = Math.ceil(low / ticks); n * ticks <= high; n++) {
        const at = toScreen(n * ticks);
        if (horizontal) canvas.drawLine(at, y + h - 3 * dpr, at, y + h, this.paint);
        else canvas.drawLine(x + w - 3 * dpr, at, x + w, at, this.paint);
      }
      this.paint.setColor(ck.Color4f(muted.r, muted.g, muted.b, 0.6));
      for (let n = Math.ceil(low / step); n * step <= high; n++) {
        const at = toScreen(n * step);
        if (horizontal) {
          canvas.drawLine(at, y + h - 6 * dpr, at, y + h, this.paint);
          options.text?.label(canvas, said(n * step), { x: at + 3 * dpr, y: y + 2 * dpr }, { size: 9 * dpr, color: ink, weight: 500 });
        } else {
          canvas.drawLine(x + w - 6 * dpr, at, x + w, at, this.paint);
          canvas.save();
          canvas.translate(x + 2 * dpr, at - 3 * dpr);
          canvas.rotate(-90, 0, 0);
          options.text?.label(canvas, said(n * step), { x: 0, y: 0 }, { size: 9 * dpr, color: ink, weight: 500 });
          canvas.restore();
        }
      }
      if (options.pointer) {
        const at = toScreen(horizontal ? options.pointer.x : options.pointer.y);
        this.paint.setColor(ck.Color4f(accent.r, accent.g, accent.b, 1));
        if (horizontal) canvas.drawLine(at, y, at, y + h, this.paint);
        else canvas.drawLine(x, at, x + w, at, this.paint);
      }
      canvas.restore();
    }
  }
}
